import styled from '@emotion/styled'
import styles from '@mathflat/common/style'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'

import useRepository from '@/hooks/useRepository'
import { envUtils } from '@/utils/envUtils'

import { ContractModalService } from '../ContractModal.service'
import { type ContractInfo, ContractPdfService } from './_service.ts/ContractPdf.service'

const {
  colors: { gray },
} = styles

const ContractPdf = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const bottomMarkerRef = useRef<HTMLDivElement>(null)
  const contractModalService = useRepository(ContractModalService)
  const service = useRepository(ContractPdfService)

  useEffect(() => {
    if (service.pdfClient.connected && iframeRef.current?.contentWindow) {
      const data: ContractInfo = service.pdfDataRenderData
      console.info('[스쿨플랫 - 계약서] PDF 렌더러에 데이터를 전송하였습니다.')
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ setContractData: data }), '*')
    }
  }, [service.contractInfo, service.pdfClient.connected])

  useEffect(() => {
    const listener = (e) => {
      if (e.origin.startsWith(window.location.origin)) {
        return
      }
      if ('ready' in e.data) {
        console.info('[스쿨플랫 - 계약서:ready] PDF 렌더러와 연결되었습니다.')
        runInAction(() => {
          service.pdfClient.connected = true
        })
      }
      if ('contractPageInfo' in e.data) {
        const contractPdfInfo = JSON.parse(e.data.contractPdfInfo) as ContractInfo
        console.info('[스쿨플랫 - 계약서] PDF 페이지 정보를 수신하였습니다.', contractPdfInfo)
        runInAction(() => {
          service.contractPdfInfo = contractPdfInfo
        })
      }
    }
    window.addEventListener('message', listener)
    return () => {
      service.pdfClient.connected = false
      window.removeEventListener('message', listener)
    }
  }, [])

  useEffect(() => {
    const viewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            contractModalService.viewPdfContract.isDirty = true
            contractModalService.viewPdfContract.isValid = true
          }
        })
      },
      {
        root: scrollRef.current,
        threshold: 1.0, // 요소가 100% 보일 때 콜백 실행
      },
    )

    if (
      contractModalService.viewPdfContract.isDirty &&
      contractModalService.viewPdfContract.isValid
    ) {
      return
    }
    if (bottomMarkerRef.current) {
      viewObserver.observe(bottomMarkerRef.current)
    }

    return () => {
      if (bottomMarkerRef.current) {
        viewObserver.unobserve(bottomMarkerRef.current)
      }
    }
  }, [])

  return (
    <S.Wrapper ref={scrollRef}>
      <div className="frame">
        <iframe
          style={{ width: '100%', height: 860, overflow: 'hidden' }}
          id="mathflat-contract-client"
          src={
            (envUtils.isLive
              ? `https://pdf-client.${domainProvider.TARGET_DOMAIN}`
              : `https://pdf-client-staging.${domainProvider.TARGET_DOMAIN}`) +
            '/schoolflat-contract'
          }
          ref={iframeRef}
        />
      </div>
      <div ref={bottomMarkerRef} style={{ height: 1 }}></div>
    </S.Wrapper>
  )
}

export default observer(ContractPdf)

const S = {
  Wrapper: styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    height: 510px;
    padding: 16px;
    background: ${gray.$100};
    .frame {
      overflow: hidden;
      display: block;
      margin: 4px auto;
      width: 602px;
      padding: 4px;
      height: 847px;
      border: 1px solid var(--Colors-Gray-500);
      background: #f0f0f0;
    }
  `,
}
